import { Component, ViewEncapsulation, OnInit, ViewChild, ElementRef, AfterViewChecked, OnDestroy, Output, EventEmitter, inject } from '@angular/core';
import { CarouselModule, CarouselComponent } from 'ngx-carousel-ease';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter, iif, map, of, share, Subject, switchMap, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import { Dialog } from '@angular/cdk/dialog';

import { ProfileService } from '../profile.service';
import { HomeService, State } from '../../home/home.service';
import { EncountersService } from '../../new-encounters/encounters.service';
import { ChatService } from '../../chat/chat.service';
import { AuthService } from '../../auth.service';
import { LayoutService } from '../../layout.service';

import { InfoDialogComponent } from '../../dialog/info-dialog/info-dialog.component';
import { ProfileDialogComponent } from '../../dialog/profile-dialog/profile-dialog.component';
import { BlockUserDialogComponent } from '../../dialog/block-user-dialog/block-user-dialog.component';
import { ReportUserDialogComponent } from '../../dialog/report-user-dialog/report-user-dialog.component';
import { UnmatchDialogComponent } from '../../dialog/unmatch-dialog/unmatch-dialog.component';
import { MatchDialogComponent } from '../../dialog/match-dialog/match-dialog.component';
import { LoaderComponent } from '../../shared/loader/loader.component';

import { User } from '../../shared/interfaces/user';

@Component({
  selector: 'app-profile-space',
  standalone: true,
  imports: [CarouselModule, CarouselComponent, RouterModule, CommonModule, InfoDialogComponent, MatchDialogComponent, LoaderComponent],
  templateUrl: './profile-space.component.html',
  styleUrl: './profile-space.component.scss',
  encapsulation: ViewEncapsulation.None,
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    'class': 'space'  // <-- It should be unique for each component you have encapsulation off
  }
})
export class ProfileSpaceComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('hobbiesContainer') hobbiesContainer!: ElementRef
  @Output() emitInfoDialogType = new EventEmitter<string>()

  profile!: User | null
  userAge!: number
  hobbyWidth!: number
  infoDialogType = ''
  relationshipStatus = ''
  dialog = inject(Dialog)
  match = false
  showWaitingStatus = false
  futureRelationshipStatus = ''
  private readonly _destroyed = new Subject<void>()
  isDesktop = this.layoutService.isDesktop
  profileLoading = false
  notSubscribed = false
  showInfoDialog = false
  user!: User | null
  user$ = this.profileService.profileId.pipe(
    takeUntil(this._destroyed),
    map(id => {
      return id
    }),
    switchMap((id: number| null) => {
      return this.authService.getProfile(id!)
    }),
    share(),
    filter(user => !!user),
    map((user: User) => {
      this.profile = user
      this.profileService.profile.next(user)
      if(this.profile && this.profile.birthdate){
        const [y,m,d] = this.profile.birthdate.split('-')
        const date = `${y}-${m}-${d}`
        const birthDate = dayjs(date)
  
        this.userAge = dayjs().diff(birthDate, 'year')
      }
      return user
    })
  )

  constructor(
    private profileService: ProfileService,
    private homeService: HomeService,
    private encountersService: EncountersService,
    private chatService: ChatService,
    private router: Router,
    private authService: AuthService,
    private layoutService: LayoutService
  ) { }

  ngOnInit(): void {
    this.profileLoading = true

    this.authService.authMe.subscribe(res => this.user = res)

    this.user$.pipe(
      switchMap((user) => {
        return iif(() => user.already_seen! ,
          of(null),
          this.encountersService.markProfileAsSeen(user!.id)
        )
      }),
      switchMap(() =>  this.encountersService.getRelationshipStatus(this.profile!.id))
    ).subscribe((status: string) => {
      this.profileLoading = false
      this.relationshipStatus = status

      if(this.relationshipStatus === 'waiting') this.showWaitingStatus = true
    })
  }

  ngAfterViewChecked(){
    if(this.isDesktop() && this.hobbiesContainer){
      const bounds = this.hobbiesContainer.nativeElement.getBoundingClientRect()
      const hobbiesContainerWidth = bounds.right - bounds.left
      this.hobbyWidth = (hobbiesContainerWidth) / 4 - 8
    } else {
      const screenWidth = window.screen.width
      this.hobbyWidth = (screenWidth - 48) / 3 - 8
    }
  }

  getStyles() {
    return {
      'width': this.hobbyWidth + 'px',
      'height': this.hobbyWidth + 'px',
    }
  }

  closeDialog(event: boolean){
    this.homeService.dialogDisplayed.next(!event)
    this.infoDialogType = ''
  }

  openProfileDialog(e: Event) {
    e.stopPropagation()
    this.dialog.open(ProfileDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
    }).closed.pipe(
      filter(res => !!res)
    ).subscribe((res) => {
      console.log(res)
      if(res === 'unmatch'){
        this.unmatchDialog()
      } else if(res === 'block'){
        this.blockUserDialog()
      } else {
        this.blockAndReportUserDialog()
      }
    })
  }

  unmatchDialog() {
    this.dialog.open(UnmatchDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
    }).closed.pipe(
      filter(res => !!res)
    ).subscribe(() => {
      if(!this.profile) return

      this.homeService.deleteRelationship(this.profile.id).subscribe(res => {
        console.log(res)
        this.relationshipStatus = 'notfriend'
        this.chatService.messageSent.next(null)
        this.chatService.relationshipDeleted.next(true)
      })
    })
  }

  matchDialog() {
    this.dialog.open(MatchDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
      data: this.profile
    }).closed.pipe(
      filter(res => !!res)
    ).subscribe(() => {
      if(!this.profile) return

      this.contactProfile(this.profile.id)
    })
  }

  blockUserDialog() {
    this.dialog.open(BlockUserDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
    }).closed.pipe(
      filter(res => !!res)
    ).subscribe(() => {
      if(!this.profile) return
      this.homeService.updateRelationship(this.profile.id, State.blocked).subscribe(res => console.log(res))
    })
  }

  blockAndReportUserDialog() {
    this.dialog.open(ReportUserDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
    }).closed.pipe(
      filter(res => !!res)
    ).subscribe(() => {
      if(!this.profile) return

      this.homeService.createReportBetweenUsers(this.profile.id, 'user').subscribe(() => {
        if(!this.profile) return
        this.homeService.updateRelationship(this.profile.id, State.blockedWithReport).subscribe(res => console.log(res))
      })
    })
  }

  rejectProfile(userId: number){
    console.log('rejectProfile', userId)
    // state set to refused on the front end. If relationship does not exist, it stays at refused. Otherwise, if already refused, it is transformed into rejected by the back end
    this.homeService.updateRelationship(userId, State.refused).subscribe(() => {
      this.router.navigate(['/home'])
      this.homeService.toggleSidenav.next(false)
    })
  }

  likeProfile(userId: number){
    console.log('likeProfile ', userId)

    // The user can like up to 5 times without being registered (number of likes checked on back side). If the user is not registed and he reached the maximum number of free likes, we show the dialog. 
    // By clicking on the button, the user is redirected to the landing page.

    // if the user is registered, we update the relationship
    let state!: State
    if(this.relationshipStatus === 'notfriend'){// the relationship does not exist
      state = State.accepted
      this.futureRelationshipStatus = 'waiting'// logged-in user likes another profile
    } else if (this.relationshipStatus === 'pending') {// logged-in user's profile has been liked
      state = State.validated
      this.futureRelationshipStatus = 'friend'
    }
    
    this.homeService.updateRelationship(userId, state).pipe(
      switchMap(() => {
        console.log('return switch map')
        // if the relationship was waiting, it becomes accepted/pending. If it was already pending, it becomes validated/friend
        return this.encountersService.getRelationshipStatus(this.profile!.id)})
    ).subscribe({
      next: (res) => {
        this.relationshipStatus = res
        this.profileService.setProfileLiked(this.profile)
      }, 
      error: (err) => {
        if(err.error.error_code === 32){ // "message" : "This user is not subscribed" sent back by /relationship/easy
          this.notSubscribed = true
          this.showInfoDialog = true
          this.infoDialogType = 'likeProfile'
          this.homeService.dialogDisplayed.next(true)
        } else {
          console.error(err)
        }
      },
      complete: () => { 
        if(this.futureRelationshipStatus === 'waiting'){
          this.showWaitingStatus = true
        } else if(this.futureRelationshipStatus === 'friend'){
          this.match = true
          this.matchDialog()
        }
      
        setTimeout(() => {
          this.profileService.setProfileLiked(null)
        }, 3000)
      }
    })
  }

  closeInfoDialog(e: boolean){
    this.showInfoDialog = !e
    this.infoDialogType = ''
    this.homeService.dialogDisplayed.next(false)
  }

  contactProfile(relatedUserId: number){
    console.log('contactProfile: ', relatedUserId)
    this.chatService.relatedUserId.next(relatedUserId)

    /* 
    Direct messages
    This allows M and F users to send a direct message (feature only available for registered men and all women).

    User A sends a direct message to user B --> Both users are automatically put in contact (autovalidated relationship) 
    + user B receives the messages + when selecting the conversation, user B sees a message asking to accept or refuse the conversation with user A.

    If user B refuses --> We delete the relationship and users are not anymore in contact with one another.
    User B cannot see user A anymore in his contacts + user B cannot see the conversation anymore.
    User A cannot see user B anymore in his contacts BUT he still can see the conversation (and send messages, which user B will never receive).
    If user B accepts --> We keep the relationship and users can keep sending messages to each other.
    */

    this.encountersService.getRelationshipStatus(relatedUserId).subscribe(status => {
      // check the user's sex 
      if(this.user?.sex === 'man'){
        // the user is a man
        if(this.user?.isSubscribed){
          // if user is registered, then he can send a direct message 

          // check if we already have a conversation with the relatedUser (woman).
          // if so, navigate to /chat/conversation/ + talkId
          // if not, navigate to /chat/conversation/new- + relatedUserId. The new conversation creation is done when sending the first message.
          this.authService.getProfile(relatedUserId).subscribe((user: User) => {
            // check if the user is already in contact with the relatedUser  
            if(user.talking_id) {
              this.router.navigate(['/home/my-matches/chat/conversation/' + user.talking_id])
              this.chatService.conversationToShow = user.talking_id
              this.homeService.pageDisplayed.next('my-matches')
            } else {
              this.router.navigate(['/home/my-matches/chat/conversation/new-' + relatedUserId])
              this.chatService.conversationToShow = 0
              this.homeService.pageDisplayed.next('my-matches')
            }
          })
          
          // this.openTalk()
          // this.chatService.relatedUserId.next(relatedUserId)
        } else {
          if(status === 'friend'){
            // match existing between users
            // check if we already have a conversation with the relatedUser (woman).
            // if so, navigate to /chat/conversation/ + talkId
            // if not, navigate to /chat/conversation/new- + relatedUserId. The new conversation creation is done when sending the first message.
            this.authService.getProfile(relatedUserId).subscribe((user: User) => {
              if(user.talking_id) {
                this.router.navigate(['/home/my-matches/chat/conversation/' + user.talking_id])
                this.chatService.conversationToShow = user.talking_id
                this.homeService.pageDisplayed.next('my-matches')
              } else {
                this.router.navigate(['/home/my-matches/chat/conversation/new-' + relatedUserId])
                this.chatService.conversationToShow = 0
                this.homeService.pageDisplayed.next('my-matches')
              }
            })
            
            // this.openTalk()
            // this.chatService.relatedUserId.next(relatedUserId)
          } else {
            // if not, a dialog is opened to redirect the user to the LP
            // if the user is not registered
            // the dialog is displayed. By clicking on the button, the user is redirected to the landing page
            this.infoDialogType = 'contactProfile'
            this.homeService.dialogDisplayed.next(true)
          }
        }
      } else {
        // the user is a woman
        console.log('woman')
        // she can send a direct message 
        // check if we already have a conversation with the relatedUser (man).
        // if so, redirect to /chat/conversation/ + talkId
        // if not, redirect to /chat/conversation/new- + relatedUserId. The new conversation creation is done when sending the first message.
        this.authService.getProfile(relatedUserId).subscribe((user: User) => {
          if(user.talking_id) {
            this.router.navigate(['/home/my-matches/chat/conversation/' + user.talking_id])
            this.chatService.conversationToShow = user.talking_id
            this.homeService.pageDisplayed.next('my-matches')
            this.chatService.setConversationToShow(user.talking_id)
          } else {
            this.router.navigate(['/home/my-matches/chat/conversation/new-' + relatedUserId])
            this.chatService.conversationToShow = 0
            this.homeService.pageDisplayed.next('my-matches')
          }
        })
        
        // console.log('toto')
        // this.openTalk()
        // this.chatService.relatedUserId.next(relatedUserId)
      }
    })
  }

  openTalk(){
    this.chatService.toggleSidenav.next(true)
    this.homeService.pageDisplayed.next('my-matches')
    this.homeService.dialogDisplayed.next(false)
  }

  ngOnDestroy(){
    this._destroyed.next()
    this._destroyed.complete()
  }
}
